//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Container, Grid, CircularProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { PATH_ADVERTISE } from '../../system/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { BASE_URL, UPLOADED_VIDEO_STATUS_APPROVED } from '../../common/constants';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import ImagePreviewer from './ImagePreviewer';
import AfitdCropper from '../../common/elemental/AfitdCropper';
import ArrowRightAltIcon from '@material-ui/icons/ArrowForward';
import ArrowBack from '@material-ui/icons/ArrowBack';
import { createStyles, withStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';
import { detectInappropriatePicture, extractBadContentFromResult, simplifySighEngineResponse } from '../../common/sightEngineHelper';
import { uploadAdPicture } from '../../common/services/adServices';
//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: { padding: 20 },
    exampleWithNoPropsOnlyStyles: { color: 'red', marginTop: 30, marginBottom: 40 },
    nonReactElementExample: { color: 'blue', marginTop: 50, cursor: 'pointer' },
    container: {
        width: '100%',
        // height: '640px',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '48px 112px 48px 112px',
        [theme.breakpoints.down('sm')]: { padding: '44px 0px' },
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    titleContainer: {
        padding: '0 0 19px 0',
        [theme.breakpoints.down('sm')]: { padding: '0px' },
    },
    brandingLogoBody: {
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column', marginTop: 15 },
    },
    logoDiv: {
        marginLeft: '24px',
        marginTop: '-55px',
        [theme.breakpoints.down('sm')]: { marginLeft: '0px', marginTop: 1 },
    },
    logoDetails: {
        fontSize: '14px',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    bannerDetails: {
        marginLeft: '24px',
        marginTop: '50px',
    },
    generalDeletePicButton: {
        marginLeft: '10px',
    },
    footerButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '80px',
    },
    titleImage: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginTop: '27px',
        marginLeft: '8px',
        marginBottom: '15px',
    },
    nextButton: {
        textAlign: 'right',
        marginTop: '20px',
    },
    backButton: {
        textAlign: 'left',
        marginTop: '20px',
    },
    progressGrid: {
        textAlign: 'center',
    },
    progressGridContainerSm: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 34,
    },
    imgBeforeCropContainer: {
        margin: 'auto',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes } = params;

    const {
        desktopImage,
        setBlob,
        mobileImage,
        handleChangeDesktopUploadInput,
        handleChangeMobileUploadInput,
        cropLogoModalOpen,
        // setCropDesktopModalOpen,
        handleCloseCropModal,
        isMobileImage,
        history,
        badImageDialogOpen,
        badImageDialogMessage,
        // setBadImageDialogOpen,
        handleSaveAdPicture,
        handleCloseBadImageDialog,
        handleCloseFileSizeExceedDialog,
        pictureTestPassed,
        source,
        isProcessingImage,
        fileSizeExceedDialogOpen,
        // setFileSizeExceedDialogOpen,
        isUploading,
    } = params;

    const nextBtnDisabled = !pictureTestPassed || !source || isUploading;

    const defns = {
        uploadButton: {
            color: 'secondary',
            title: 'Upload Image',
            component: 'label',
        },

        logoUploadInput: {
            type: 'file',
            hidden: true,
            onChange: handleChangeDesktopUploadInput,
            accept: '.jpg, .png',
        },
        mobileUploadInput: {
            type: 'file',
            hidden: true,
            onChange: handleChangeMobileUploadInput,
            accept: '.jpg, .png',
        },
        cropLogoModal: {
            open: cropLogoModalOpen,
            onClose: handleCloseCropModal,
            label: 'Upload Ad Image',
            actionButtonDefns: {
                cancel: {
                    disabled: false,
                    onClick: handleCloseCropModal,
                },
                save: {
                    disabled: isProcessingImage,
                    onClick: handleSaveAdPicture,
                    label: isProcessingImage ? <CircularProgress size={25} /> : 'Save Changes',
                },
            },
        },
        adImageCropper: { file: isMobileImage ? mobileImage : desktopImage, setBlob },
        saveButton: {
            color: 'primary',
        },
        footerGridItem: {
            xl: 5,
            lg: 5,
            md: 5,
            sm: 6,
            xs: 6,
        },
        progressGrid: { item: true, xs: 2 },
        progress: {
            variant: 'determinate',
            value: 40,
        },
        nextButton: {
            color: 'primary',
            disabled: nextBtnDisabled,
            onClick: () => {
                if (!nextBtnDisabled) history.push(`${BASE_URL}${PATH_ADVERTISE}/step3`);
            },
        },
        backButton: {
            onClick: () => history.push(`${BASE_URL}${PATH_ADVERTISE}/step1`),
        },
        gridBody1: {
            container: true,
            spacing: 1,
            alignItems: 'center',
        },
        badImageDialog: {
            title: 'Inappropriate Images are not Allowed!',
            children: (
                <AfitdTypography>
                    We detected <b>{badImageDialogMessage}</b> in your picture which does not meet our community guidelines. Inappropriate Images cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: badImageDialogOpen,
            onClose: handleCloseBadImageDialog,
        },
        fileSizeExceedDialog: {
            title: 'File is too Big!',
            children: <AfitdTypography>Your uploaded file size is more than 5MB, Please select a file with max 5MB size.</AfitdTypography>,
            open: fileSizeExceedDialogOpen,
            onClose: handleCloseFileSizeExceedDialog,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const BorderLinearProgress = withStyles(theme =>
    createStyles({
        root: {
            height: 10,
            borderRadius: 5,
        },
        colorPrimary: {
            backgroundColor: '#F0F2F6',
        },
        bar: {
            borderRadius: 5,
            backgroundColor: '#1D8A8B',
        },
    }),
)(LinearProgress);

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const Step4Page = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();

    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { scrollToPageTop, setSnackbarErrorMessage, setSnackbarMessage, setAdImageUrl, customerData } = layoutContext;
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    const adCusId = _get(customerData, 'adCusId', null);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);
    const [desktopImage, setDesktopImage] = useState('');
    const [mobileImage, setMobileImage] = useState('');
    const [blob, setBlob] = useState(null);
    const [cropLogoModalOpen, setCropDesktopModalOpen] = useState(false);
    const [source, setSource] = useState('');
    // const [sourceBanner, setSourceBanner] = useState('');
    const [isMobileImage, setIsMobileImage] = useState(false);
    const [badImageDialogOpen, setBadImageDialogOpen] = useState(false);
    const [fileSizeExceedDialogOpen, setFileSizeExceedDialogOpen] = useState(false);
    const [badImageDialogMessage, setBadImageDialogMessage] = useState([]);
    const [pictureTestPassed, setPictureTestPassed] = useState(false);
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const [isUploading, setIsUploading] = React.useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    useEffect(() => {
        if (!adCusId) history.push(`${BASE_URL}${PATH_ADVERTISE}/start`);
        // don't need to run multiple Times
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleBlobCreation = () => {
        if (blob) {
            let url = URL.createObjectURL(blob);
            setSource(url);
        }
        setCropDesktopModalOpen(false);
    };

    const handleChangeDesktopUploadInput = evt => {
        const file = evt.target.files[0];
        if (file?.size < 5000000) {
            setDesktopImage(file);
            setIsMobileImage(false);
            setCropDesktopModalOpen(true);
        } else {
            setFileSizeExceedDialogOpen(true);
        }
    };
    const handleChangeMobileUploadInput = evt => {
        const file = evt.target.files[0];
        if (file?.size < 5000000) {
            setMobileImage(file);
            setIsMobileImage(true);
            setCropDesktopModalOpen(true);
        } else {
            console.log('file is huge');
        }
    };

    const handleCloseCropModal = () => {
        setCropDesktopModalOpen(false);
    };

    const handleDeleteDesktopImage = () => {
        setSource('');
        setDesktopImage('');
    };

    const callBackOnPictureSave = url => {
        setAdImageUrl(url);
        handleCloseCropModal();
        setIsUploading(false);
    };

    const uploadPicture = async () => {
        setIsUploading(true);
        await uploadAdPicture({ file: blob, adCusId, setSnackbarErrorMessage, setSnackbarMessage, callBack: callBackOnPictureSave });
    };

    const handleSaveAdPicture = async () => {
        setIsProcessingImage(true);
        // send to Sight Engine
        const sightEngineResponse = await detectInappropriatePicture(desktopImage);
        if (sightEngineResponse?.status !== 'failure') {
            const { seResult, pictureStatus } = await simplifySighEngineResponse(sightEngineResponse);
            if (pictureStatus === UPLOADED_VIDEO_STATUS_APPROVED) {
                setPictureTestPassed(true);
                handleBlobCreation();
                await uploadPicture();
            } else {
                setPictureTestPassed(false);
                await extractBadContentFromResult({ seResult, setBadImageDialogMessage, setBadImageDialogOpen });
            }
            setIsProcessingImage(false);
        }
    };

    const handleCloseBadImageDialog = async () => {
        if (!pictureTestPassed) {
            setFileSizeExceedDialogOpen(false); // why???
            setSource('');
            setBlob(null);
            handleCloseCropModal();
            setBadImageDialogOpen(false);
            setBadImageDialogMessage('');
        }
    };

    const handleCloseFileSizeExceedDialog = () => {
        setSource('');
        setBlob(null);
        setFileSizeExceedDialogOpen(false);
    };

    // const handleDeleteMobileImage = () => {
    //     setSourceBanner('');
    //     setMobileImage('');
    // };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        desktopImage,
        mobileImage,
        setBlob,
        isMobileImage,
        handleChangeDesktopUploadInput,
        handleChangeMobileUploadInput,
        cropLogoModalOpen,
        setCropDesktopModalOpen,
        handleCloseCropModal,
        history,
        badImageDialogOpen,
        badImageDialogMessage,
        setBadImageDialogOpen,
        handleSaveAdPicture,
        handleCloseBadImageDialog,
        handleCloseFileSizeExceedDialog,
        pictureTestPassed,
        source,
        isProcessingImage,
        fileSizeExceedDialogOpen,
        setFileSizeExceedDialogOpen,
        isUploading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <Container {...defns.container}>
                <div {...defns.heading}>Upload Ad Image</div>
                <div {...defns.header}>
                    <div {...defns.titleContainer}>
                        <AfitdTypography {...defns.titleText} />
                    </div>
                </div>
                <div {...defns.brandingLogoBody}>
                    <div>
                        <p {...defns.titleImage}>Desktop</p>
                        <ImagePreviewer bannerUrl={source} />
                    </div>
                    <div {...defns.logoDiv}>
                        <h4>
                            <b>Upload Ad Images</b>
                        </h4>
                        <p {...defns.logoDetails}>
                            JPG and PNG are the accepted file types with <br /> max size of 5 MB.
                        </p>
                        <div>
                            <AfitdButton {...defns.uploadButton}>
                                {source === '' ? 'Upload Image' : 'Change'}
                                <input {...defns.logoUploadInput} />
                            </AfitdButton>
                            {source !== '' && (
                                <AfitdButton onClick={handleDeleteDesktopImage} {...defns.generalDeletePicButton}>
                                    Delete
                                </AfitdButton>
                            )}
                        </div>
                    </div>
                </div>
                {/* <div {...defns.brandingLogoBody}>
                    <div>
                        <p {...defns.titleImage}>Mobile</p>
                        <ImagePreviewer bannerUrl={sourceBanner} />
                    </div>
                    <div>
                        <div {...defns.logoDiv}>
                            <h4>
                                <b>Upload Ad Images</b>
                            </h4>
                            <p {...defns.logoDetails}>
                                PDF, DOCX, XLSX, image, etc. files with <br /> max size of 15 MB.
                            </p>
                            <div>
                                <AfitdButton {...defns.uploadButton}>
                                    {sourceBanner === '' ? 'Upload Image' : 'Change'}
                                    <input {...defns.mobileUploadInput} />
                                </AfitdButton>
                                {sourceBanner !== '' && (
                                    <AfitdButton onClick={handleDeleteMobileImage} {...defns.generalDeletePicButton}>
                                        Delete
                                    </AfitdButton>
                                )}
                            </div>
                        </div>
                    </div>
                </div> */}
                <br />
                <Grid {...defns.gridBody1}>
                    <Grid {...defns.footerGridItem}>
                        <div>
                            <AfitdButton {...defns.backButton}>
                                <ArrowBack fontSize="small" />
                                &nbsp; Back {` `}
                            </AfitdButton>
                        </div>
                    </Grid>
                    {smMatches ? null : (
                        <Grid {...defns.progressGrid}>
                            <p>
                                <b>2 of 5</b>
                            </p>
                            <BorderLinearProgress {...defns.progress} />
                        </Grid>
                    )}

                    <Grid {...defns.footerGridItem}>
                        <div {...defns.nextButton}>
                            <AfitdButton {...defns.nextButton}>
                                Next {` `}
                                &nbsp;
                                <ArrowRightAltIcon fontSize="small" />
                            </AfitdButton>
                        </div>
                    </Grid>
                    {smMatches && (
                        <div {...defns.progressGridContainerSm}>
                            <Grid {...defns.progressGrid}>
                                <p>
                                    <b>2 of 5</b>
                                </p>
                                <BorderLinearProgress {...defns.progress} />
                            </Grid>
                        </div>
                    )}
                </Grid>

                {cropLogoModalOpen && (
                    <AfitdEditDialog {...defns.cropLogoModal}>
                        <div {...defns.imgBeforeCropContainer}>
                            <AfitdCropper {...defns.adImageCropper} />
                        </div>
                    </AfitdEditDialog>
                )}
                <AfitdMessageDialog {...defns.badImageDialog} />
                <AfitdMessageDialog {...defns.fileSizeExceedDialog} />
            </Container>
        </div>
    );
};

export default Step4Page;
